import React, { useState, useMemo } from "react";
import DataTable from "components/DataTable";
import PageWrapper from "components/PageWrapper";
import ShowToast from "components/ShowToast";
import { fetchWrapper } from "utils/apiFetch";
import { useTableData } from "../../hooks/useTableData";
import { walletSettlementReportsConfig } from "config/tableConfigs";
import { useSession } from "hooks/useSession";
import ManualPayoutStatusModal from "./MannualPayoutStatusModal";
import PayoutReceiptModal from "./PayoutReceipt";

const WalletSettlementReportsPage = () => {
  const { userid, role } = useSession();
  const userRole = role === "Platform User" ? "Admin" : role;
  const isAdmin = userRole === "Admin";
  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [checkingStatusId, setCheckingStatusId] = useState(null);

  const getTodayRange = () => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    return {
      start: todayStr,
      end: todayStr,
    };
  };

  const { start, end } = getTodayRange();

  const [apiUrl, setApiUrl] = useState(
    `https://pro.mokshiq.in/get_payout_report?user=${userid}&filter_date=${start}&end_date=${end}`
  );

  const tableData = useTableData(apiUrl, 0, walletSettlementReportsConfig);

  const refreshTable = () => {
    // cache-bust to force refetch — same trick used by handleUpdated below
    setApiUrl((prev) => prev.split("&_r=")[0] + "&_r=" + Date.now());
  };

  const handleUpdated = () => {
    // refresh the table after a manual admin status change
    refreshTable();
  };

  // ── Cyrus payout: "check status" instead of manual admin override ──
  // Cyrus resolves pending payouts via its own checkstatus API (and the
  // async callback), so there's nothing for an admin to manually flip here
  // the way ManualPayoutStatusModal does for Softmint. Clicking View on a
  // cyrus_payout row just asks Cyrus for the current status and refreshes
  // the row — CyrusCheckStatus on the backend already finalizes the
  // wallet/commission side via _finalize_cyrus_payout_success if it
  // confirms success.
  const handleCyrusCheckStatus = async (row) => {
    const orderId = row.ref_no || row.ref_id || row.orderId;
    if (!orderId) {
      ShowToast("This row has no orderId to check", "warning");
      return;
    }

    setCheckingStatusId(row.id);
    try {
      const formData = new FormData();
      formData.append("orderId", orderId);

      const res = await fetchWrapper("https://pro.mokshiq.in/billpay_check_status", {
        method: "POST",
        body: formData,
      });

      if (res?.status) {
        ShowToast(`Status: ${res.message || "Checked"}`, "success");
      } else {
        ShowToast(res?.message || "Status check failed", "danger");
      }
      refreshTable();
    } catch (e) {
      ShowToast("Something went wrong while checking status", "danger");
    } finally {
      setCheckingStatusId(null);
    }
  };

  const handleView = (row) => {
    if (row.product === "cyrus_payout") {
      setSelectedRow(row);
      setShowReceipt(true);
    }

    // if (!isAdmin) return;
    // setSelectedTxn(row);
    // setShowModal(true);
  };

  const handleDateFilter = (s, e) => {
    setApiUrl(
      `https://pro.mokshiq.in/get_payout_report?user=${userid}&filter_date=${s}&end_date=${e}`
    );
  };

  // 🔥 ROLE → COLUMN BINDING (SAME AS DMT)
  const filteredConfig = useMemo(() => {
    const base = { ...walletSettlementReportsConfig };

    const roleMap = {
      Retailer: [
        "id",
        "ref_no",
        "ack_no",
        "product",
        "account_number",
        "holder_name",
        "number",
        "amount",
        "created_at",
        "status",
        "Opening Balance",
        "Commission",
        "TDS",
        "Charge",
        "GST",
        "Closing Balance",
        "bank_rrn",
      ],

      Distributer: [
        "id",
        "ref_no",
        "ack_no",
        "product",
        "account_number",
        "holder_name",
        "number",
        "amount",
        "created_at",
        "status",
        "openingBalanceDistributor",
        "commission",
        "tds",
        "charge",
        "gst",
        "closingBalanceDistributor",
        "bank_rrn",
      ],

      "Super Distributer": [
        "id",
        "ref_no",
        "ack_no",
        "product",
        "account_number",
        "holder_name",
        "number",
        "amount",
        "created_at",
        "status",
        "openingBalanceSuperDistributor",
        "commission",
        "tds",
        "charge",
        "gst",
        "closingBalanceSuperDistributor",
        "bank_rrn",
      ],
      "White label": [
        "id",
        "bcName", // Agent Name from your JSON
        "ref_no",
        "ack_no",
        "account_number", // From your JSON
        "holder_name", // From your JSON
        "amount",
        "status",

        // White Label's Own Financials
        "Opening Balance",
        "Commission",
        "TDS",
        "Charge",
        "GST",
        "Closing Balance",

        // Agent's (BC) Financials
        "Opening Balance BC",
        "BC Commission",
        "BC TDS",
        "BC Charge",
        "Closing Balance BC",

        "bank_rrn",
        "created_at",
      ],

      Admin: [
        "id",
        "ref_no",
        "ack_no",
        "product",
        "number",
        "amount",
        "created_at",
        "status",
        "bcName",
        "openingBalanceAdmin",
        "commissionAdmin",
        "tdsAdmin",
        "chargeAdmin",
        "gstAdmin",
        "closingBalanceAdmin",
        "walletTxn",
        "txnFlow",
        "bank_rrn",
      ],

      "Main Admin": walletSettlementReportsConfig.columns.map((c) => c.key),
    };

    const allowedKeys = roleMap[userRole] || [];
    base.columns = base.columns.filter((c) => allowedKeys.includes(c.key));

    return base;
  }, [userRole]);
  // const handleView = (row) => {
  //   setSelectedRow(row);
  //   setShowReceipt(true);
  // };
 
  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedRow(null);
  };

  return (
    <div>
      {(loading || checkingStatusId) && (
        <div className="page-loader-overlay d-flex justify-content-center align-items-center">
          <div className="spinner-border text-primary" />
        </div>
      )}

      <PageWrapper config={walletSettlementReportsConfig} onDateFilter={handleDateFilter}>
        <DataTable
          config={filteredConfig}
          tableData={tableData}
          onView={handleView}
          onEdit={() => {}}
          onDelete={() => {}}
          onExport={() => {}}
          onDeleteSelected={() => {}}
        />
      </PageWrapper>
      {isAdmin && (
        <ManualPayoutStatusModal
          show={showModal}
          transaction={selectedTxn}
          onClose={() => setShowModal(false)}
          onUpdated={handleUpdated}
        />
      )}

<PayoutReceiptModal
  isOpen={showReceipt}
  onClose={handleCloseReceipt}
  payoutId={selectedRow?.ref_no || selectedRow?.ref_id}
/>
    </div>
  );
};

export default WalletSettlementReportsPage;